import { Icon } from "@legal-500/legal500-design-system";
import { useEffect, useId, useRef, useState } from "react";
import type { ReviewStatus } from "../../../api/pendingCompanies.service";
import type { ReviewAction } from "./ReviewPanel";
import styles from "./Review.module.css";

const DESCRIPTIONS: Partial<Record<ReviewAction, string>> = {
  approve: "Create a company from this entry, or link it to an existing one.",
  reject: "Decline this entry without creating a company.",
  relink: "Point this entry at a different existing company.",
};

export function EntryActions({
  status,
  onSelect,
}: {
  status: ReviewStatus;
  onSelect: (action: ReviewAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const idBase = useId();
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);
  const choices: { label: string; action: ReviewAction }[] =
    status === "approved"
      ? [{ label: "Change linked company", action: "relink" }]
      : status === "pending"
        ? [
            { label: "Approve", action: "approve" },
            { label: "Reject", action: "reject" },
          ]
        : [{ label: "Approve", action: "approve" }];
  return (
    <div
      ref={root}
      className={styles.actionMenu}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}
      onKeyDown={event => {
        if (event.key === "Escape" && open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          trigger.current?.focus();
        }
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
          event.preventDefault();
          if (!open) {
            setOpen(true);
            return;
          }
          const items = [
            ...(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
          ];
          const index = items.indexOf(document.activeElement as HTMLElement);
          items[
            event.key === "Home"
              ? 0
              : event.key === "End"
                ? items.length - 1
                : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length
          ]?.focus();
        }
      }}
    >
      <button
        ref={trigger}
        type="button"
        className={styles.splitButton}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        <span className={styles.splitLabel}>Actions</span>
        <span className={styles.splitDivider} aria-hidden="true" />
        <span className={styles.splitChevron} aria-hidden="true">
          <Icon icon="chevron-down" size="small" />
        </span>
      </button>
      {open && (
        <div role="menu" aria-label="Entry actions" className={styles.menuItems}>
          {choices.map(choice => (
            <button
              key={choice.action}
              type="button"
              role="menuitem"
              aria-label={choice.label}
              aria-describedby={`${idBase}-${choice.action}`}
              className={`${styles.menuItem} ${
                choice.action === "approve"
                  ? styles.approveItem
                  : choice.action === "reject"
                    ? styles.rejectItem
                    : ""
              }`}
              onClick={() => {
                setOpen(false);
                onSelect(choice.action);
              }}
            >
              <span className={styles.menuItemLabel}>{choice.label}</span>
              <span id={`${idBase}-${choice.action}`} className={styles.menuItemDescription}>
                {DESCRIPTIONS[choice.action]}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
